import { Button } from "@/components/ui/button";
import { TBillingInterval } from "@/types/app";
import { cn } from "@/utils/cn";
import formatCurrency from "@/utils/formatCurrency";
import { formatPriceOrNA } from "@/utils/formatPriceOrNA";
import { getBillingLabel } from "@/utils/getBillingLabel";
import { Plus } from "@pkg/icons";
import { TCartItem } from "../types.checkout";

const PlanCardUIName = ({ children }: { children: React.ReactNode }) => (
  <h3
    data-component="PlanCardUIName"
    className="font-archivo text-base leading-[140%] font-normal tracking-[0.16px] text-[#474747] sm:text-xl sm:tracking-[-0.2px]"
  >
    {children}
  </h3>
);

const PlanCardUIDiscountBadge = ({ text }: { text: string }) => (
  <div
    data-component="PlanCardUIDiscountBadge"
    className="flex h-6 items-center rounded-full bg-[#1F6A46] px-1.5 py-1"
  >
    <span className="font-archivo text-[10px] leading-4 font-bold tracking-[0.5px] text-[#F4F4F4] uppercase">
      {text}
    </span>
  </div>
);

const PlanCardUIRecommendedBadge = ({ text }: { text: string }) => (
  <div
    data-component="PlanCardUIRecommendedBadge"
    className="flex h-6 items-center rounded-sm border border-[#DCBF49] bg-[#F8F2DB] px-1.5 py-1"
  >
    <span className="font-archivo text-xs leading-[120%] font-normal tracking-[0.24px] text-[#2C260F]">
      {text}
    </span>
  </div>
);

const PlanCardUIDescription = ({ children }: { children: React.ReactNode }) => (
  <p
    data-component="PlanCardUIDescription"
    className="mt-1 font-archivo text-sm leading-[170%] font-light tracking-[0.14px] text-[#323232]"
  >
    {children}
  </p>
);

const PlanCardUIOriginalPrice = ({
  price,
  currency,
}: {
  price: number;
  currency: string;
}) => (
  <span
    data-component="PlanCardUIOriginalPrice"
    className="font-archivo text-sm leading-[140%] font-light tracking-[-0.18px] text-error-primary line-through"
  >
    {formatCurrency(price, currency, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}
  </span>
);

const PlanCardUIPricing = ({
  price,
  listPrice,
  billingInterval,
  currency,
}: {
  price: number | undefined;
  listPrice?: number;
  billingInterval: TBillingInterval;
  currency: string;
}) => {
  if (price !== undefined && (!price || !Number.isFinite(price))) return null;

  const showListPrice =
    price !== undefined &&
    listPrice != null &&
    Number.isFinite(listPrice) &&
    listPrice > price;

  return (
    <div data-component="PlanCardUIPricing" className="flex items-end gap-1">
      {showListPrice && (
        <PlanCardUIOriginalPrice price={listPrice} currency={currency} />
      )}
      <div className="flex items-end gap-0.5">
        <span className="font-archivo text-xl leading-[140%] font-normal tracking-[-0.2px] text-[#474747]">
          {formatPriceOrNA(price, currency, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
        </span>
        {price !== undefined && billingInterval && (
          <span className="ml-1 font-archivo text-base leading-[140%] font-light tracking-[0.16px] text-[#323232]">
            /{getBillingLabel(billingInterval)}
          </span>
        )}
      </div>
    </div>
  );
};

const PlanCardUIAddButton = () => (
  <Button data-component="PlanCardUIAddButton" variant="outline" size="icon-sm">
    <Plus className="" />
  </Button>
);

const PlanCardUIUpgradeButton = () => (
  <Button
    data-component="PlanCardUIUpgradeButton"
    variant="accent-invert"
    size="sm"
  >
    Upgrade
  </Button>
);

export type PlanCardUIProps = TCartItem & {
  planDescription: string;
  cta?: {
    type: "add" | "upgrade";
    onClick: (productRefId: string, e: React.MouseEvent<HTMLElement>) => void;
  };
  /** From the API's pricing, not assumed — the catalog is priced per country. */
  currency: string;
  badges?: {
    discount?: {
      text: string;
    };
    recommended?: {
      text: string;
    };
  };

  className?: string;
};

const PlanCardUI = ({
  id,
  name,
  planDescription,
  price,
  listPrice,
  interval: billingInterval,
  cta,
  badges,
  currency,
  className = "",
}: PlanCardUIProps) => {
  //---------------
  const ctaType = cta?.type;
  const actionLabel = cta
    ? ctaType === "upgrade"
      ? `Upgrade to ${name}`
      : `Add ${name}`
    : name;

  return (
    <div
      data-component="PlanCardUI"
      data-plan={id}
      aria-label={actionLabel}
      className={cn(
        "flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border border-edge-light bg-white p-4 text-left",
        cta &&
          "hover:cursor-pointer focus-visible:ring-2 focus-visible:ring-[#436FC6] focus-visible:ring-offset-2 focus-visible:outline-hidden",
        className,
      )}
      onClick={cta ? (e) => cta.onClick(id, e) : undefined}
    >
      <div className="flex flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <PlanCardUIName>{name}</PlanCardUIName>
          {badges?.discount?.text && (
            <PlanCardUIDiscountBadge text={badges.discount.text} />
          )}
          {badges?.recommended?.text && (
            <PlanCardUIRecommendedBadge text={badges.recommended.text} />
          )}
        </div>
        {planDescription && (
          <PlanCardUIDescription>{planDescription}</PlanCardUIDescription>
        )}
      </div>

      <div className="ml-auto flex items-center gap-4">
        <PlanCardUIPricing
          price={price}
          listPrice={listPrice}
          billingInterval={billingInterval}
          currency={currency}
        />
        {ctaType === "add" && <PlanCardUIAddButton />}
        {ctaType === "upgrade" && <PlanCardUIUpgradeButton />}
      </div>
    </div>
  );
};

export default PlanCardUI;
